import { Component, OnInit, Inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { PlatformService } from './services/platform.service';
import { Seo } from './types';
import { filter, map, Subject, takeUntil } from 'rxjs';
import { AppService } from './services/app.service';
import { ApiService } from './services/api.service';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
    title = 'Private Capital Indices';
    private destroy$ = new Subject<void>();
    init = true;

    constructor(
        public platformService: PlatformService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private appService: AppService,
        private api: ApiService
    ) {
        this.router.events
            .pipe(
                filter(e => e instanceof NavigationEnd),
                map(() => this.activatedRoute),
                map(route => {
                    while (route.firstChild) route = route.firstChild;
                    return route;
                }),
                filter(route => route.outlet === 'primary'),
                takeUntil(this.destroy$)
            )
            .subscribe(route => {
                const { key, title, description } = route.snapshot.data;

                this.appService.updateTitle(title);

                const seo: Seo = {
                    ...this.appService.defaultSeo,
                    title,
                    ...(description ? { description } : {})
                };
                this.appService.updateMetaTags(seo);

                if (!this.init) {
                    this.platformService.runInBrowser(() =>
                        this.api.trackActivity(this.router.url.split('/')[1])
                    );
                } else {
                    this.init = false;
                }
            });
    }

    ngOnInit(): void {
        // Get the 'target' query parameter from the URL
        const targetParam = this.activatedRoute.snapshot.queryParams['target'];
        const currentRoute = this.router.url.split('/').pop()?.split('?')[0];

        // Don't update targetUrl if the current route is 'login'
        if (currentRoute !== 'login') {
            this.appService.targetUrl = !!targetParam ? decodeURIComponent(targetParam) : '';
        }

        // Subscribe to query parameter changes (for reactive updates)
        this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const target = params['target'];
            const route = this.router.url.split('/').pop()?.split('?')[0];

            // Don't update targetUrl if the current route is 'login'
            if (route !== 'login') {
                this.appService.targetUrl = !!target ? decodeURIComponent(target) : '';
            }
        });

        // Guard direct DOM access for SSR (server rendering has no document).
        if (this.platformService.isBrowser()) {
            document.documentElement.setAttribute('data-theme', 'core');
            this.setDataSize();

            // Listen for window resize to update data-size responsively
            window.addEventListener('resize', () => this.setDataSize());
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setDataSize(): void {
        const size = window.innerWidth < 1024 ? 'medium' : 'large';
        document.documentElement.setAttribute('data-size', size);
    }
}
